<template>
  <Dropdown
    v-model="shown"
    :disabled
    :container
    :placement
    popper-class="ui-dropdown-columns"
    @hide="handleHide"
  >
    <template #default="{ show, hide, toggle, icon }">
      <slot :show :hide :toggle :icon :count />
    </template>
    <template #popper>
      <div class="ui-dropdown-columns__container">
        <draggable
          v-model="computedData"
          v-bind="dragOptions"
          handle=".handle"
          item-key="value"
          tag="div"
          class="ui-dropdown-columns__list"
        >
          <template #item="{ element }">
            <DropdownColumnsItem
              :model-value="modelValue?.includes(element?.value)"
              :item="element"
              :disallow-drag
              @update:model-value="handleUpdateModelValue(element?.value, $event)"
            />
          </template>
        </draggable>
      </div>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Placement } from 'floating-vue'
import draggable from 'vuedraggable'
import { orderBy } from 'lodash'

import { DropdownColumnsData } from './utils/types'

import Dropdown from './Dropdown.vue'
import DropdownColumnsItem from './DropdownColumnsItem.vue'

type Props = {
  data: DropdownColumnsData[]

  disabled?: boolean

  container?: string
  placement?: Placement
  disallowDrag?: boolean
  singleSelect?: boolean
}

const { placement = 'bottom-end', ...props } = defineProps<Props>()

const shown = defineModel<boolean>('shown', { default: false })
const modelValue = defineModel<string[] | string>()

const computedData = computed({
  get() {
    if (props.singleSelect) {
      return props.data
    }
    return orderBy(props.data, item => {
      const index = modelValue.value?.indexOf(item?.value || '')
      return index === -1 ? Infinity : index
    })
  },
  set(value) {
    modelValue.value = orderBy(modelValue.value, item => (value as DropdownColumnsData[])?.findIndex(v => v?.value === item))
  },
})

const dragOptions = computed(() => ({
  animation: 200,
  disabled: props.disallowDrag,
}))

const count = computed(() => modelValue.value?.length || 0)

const handleHide = () => {
  shown.value = false
}

const handleUpdateModelValue = (value: string, checked: boolean) => {
  if (props.singleSelect) {
    modelValue.value = checked ? value : undefined
    return
  }
  if (checked) {
    modelValue.value = [...new Set([...(modelValue.value || []), value])]
  } else {
    modelValue.value = (modelValue.value as string[])?.filter(item => item !== value)
  }
}
</script>

<style>
.ui-dropdown-columns {
  .v-popper__inner {
    @apply min-w-40 max-w-[80vw];
  }

  .v-popper__arrow-container {
    @apply hidden;
  }

  &__container {
    @apply max-h-80 overflow-y-auto;
  }

  &__list {
    @apply flex flex-col;
    @apply gap-1 px-1.5 py-1;
  }
}
</style>
